import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tempSessionAPI } from '@/lib/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Loader2, Search, Activity, Clock, Globe, Monitor,
  CheckCircle, XCircle, AlertCircle, Eye, FileText, Filter
} from 'lucide-react';
import { toast } from 'sonner';

export default function TempSessionLogsPage() {
  const { workspaceId, sessionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [actionFilter, setActionFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');

  useEffect(() => { loadLogs(); }, [sessionId, actionFilter, resultFilter]);

  const loadLogs = async () => {
    try {
      const params = {};
      if (actionFilter) params.action = actionFilter;
      if (resultFilter) params.result_filter = resultFilter;
      const res = await tempSessionAPI.getLogs(workspaceId, sessionId, params);
      setLogs(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const resultBadge = (result) => {
    const map = {
      allowed: { icon: CheckCircle, label: 'Autorisé', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      denied: { icon: XCircle, label: 'Refusé', cls: 'bg-red-50 text-red-700 border-red-200' },
      expired: { icon: AlertCircle, label: 'Expiré', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    };
    const r = map[result] || map.allowed;
    const Icon = r.icon;
    return (
      <Badge variant="outline" className={`${r.cls} text-xs`}>
        <Icon className="h-3 w-3 mr-1" /> {r.label}
      </Badge>
    );
  };

  const actionBadge = (action) => {
    const map = {
      view: { icon: Eye, label: 'Vue', cls: 'bg-blue-50 text-blue-700' },
      attempt: { icon: FileText, label: 'Tentative', cls: 'bg-purple-50 text-purple-700' },
      submit: { icon: CheckCircle, label: 'Soumission', cls: 'bg-emerald-50 text-emerald-700' },
    };
    const a = map[action] || map.view;
    const Icon = a.icon;
    return (
      <Badge variant="outline" className={`${a.cls} text-xs border-transparent`}>
        <Icon className="h-3 w-3 mr-1" /> {a.label}
      </Badge>
    );
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('fr-FR', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    } catch { return d; }
  };

  const truncateUA = (ua) => {
    if (!ua) return '—';
    if (ua.length > 60) return ua.substring(0, 57) + '...';
    return ua;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="page-container flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/workspace/${workspaceId}/temp-sessions/${sessionId}`)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Logs d'accès</h1>
            <p className="text-sm text-muted-foreground">{logs.length} entrées</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Action..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="">Toutes les actions</SelectItem>
              <SelectItem value="view">Vue</SelectItem>
              <SelectItem value="attempt">Tentative</SelectItem>
              <SelectItem value="submit">Soumission</SelectItem>
            </SelectContent>
          </Select>
          <Select value={resultFilter} onValueChange={setResultFilter}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Résultat..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="">Tous les résultats</SelectItem>
              <SelectItem value="allowed">Autorisé</SelectItem>
              <SelectItem value="denied">Refusé</SelectItem>
              <SelectItem value="expired">Expiré</SelectItem>
            </SelectContent>
          </Select>
          {(actionFilter || resultFilter) && (
            <Button variant="ghost" size="sm" onClick={() => { setActionFilter(''); setResultFilter(''); }}>
              <Filter className="h-4 w-4 mr-1" /> Réinitialiser
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card className="stat-card">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{logs.filter(l => l.action === 'view').length}</p>
                <p className="text-xs text-muted-foreground">Vues</p>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{logs.filter(l => l.result === 'allowed').length}</p>
                <p className="text-xs text-muted-foreground">Autorisés</p>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{logs.filter(l => l.result === 'denied').length}</p>
                <p className="text-xs text-muted-foreground">Refusés</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Logs Table */}
        {logs.length === 0 ? (
          <div className="empty-state py-16">
            <Activity className="empty-state-icon" />
            <h3 className="text-lg font-semibold">Aucun log</h3>
            <p className="text-sm text-muted-foreground">Les accès seront enregistrés ici</p>
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date/Heure</th>
                      <th>Action</th>
                      <th>Résultat</th>
                      <th>Contenu</th>
                      <th>IP</th>
                      <th>User-Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>
                          <span className="flex items-center gap-1 text-sm whitespace-nowrap">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                            {formatDateTime(log.accessed_at)}
                          </span>
                        </td>
                        <td>{actionBadge(log.action)}</td>
                        <td>{resultBadge(log.result)}</td>
                        <td className="text-sm">{log.item_title || '—'}</td>
                        <td>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                            <Globe className="h-3 w-3" />
                            {log.ip || '—'}
                          </span>
                        </td>
                        <td>
                          <span className="text-xs text-muted-foreground" title={log.user_agent}>
                            {truncateUA(log.user_agent)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
